import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CheckCircle2,
  CircleAlert,
  CircleDashed,
  ClipboardList,
  FileImage,
  type LucideIcon,
} from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { AddDocumentModal } from '../person/AddDocumentModal';
import { Button } from '../ui/Button';
import { Pill, type PillTone } from '../ui/Pill';
import type { MeTask, MeTaskStatus } from './meTypes';

const TASK_STATUS_STYLE: Record<
  MeTaskStatus,
  { label: string; icon: LucideIcon; tone: PillTone }
> = {
  pending: { label: 'To do', icon: CircleDashed, tone: 'neutral' },
  awaiting_review: { label: 'Under review', icon: CircleDashed, tone: 'warning' },
  changes_requested: { label: 'Changes needed', icon: CircleAlert, tone: 'danger' },
  complete: { label: 'Done', icon: CheckCircle2, tone: 'success' },
};

/**
 * Plain to-do tasks (photo evidence or mark-done). Onboarding-kind tasks
 * render in OnboardingTasks instead, so the caller filters them out.
 */
export function TasksCard({ tasks }: { tasks: MeTask[] }) {
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: async (input: {
      task_id: string;
      note?: string;
      dataUrl?: string;
    }) =>
      api(`/api/me/tasks/${input.task_id}/submit`, {
        method: 'POST',
        body: JSON.stringify({
          ...(input.note ? { note: input.note } : {}),
          ...(input.dataUrl ? { document_jpeg: input.dataUrl } : {}),
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me-view'] });
    },
  });

  async function onSubmit(
    task: MeTask,
    input: { name: string; description?: string; dataUrl: string },
  ) {
    try {
      await submit.mutateAsync({
        task_id: task.task_id,
        ...(input.description ? { note: input.description } : {}),
        dataUrl: input.dataUrl,
      });
      toast.success(
        task.approval_required
          ? `${task.title} sent for review`
          : `${task.title} marked done`,
      );
      setSubmittingId(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not submit');
      throw err;
    }
  }

  async function markDone(task: MeTask) {
    try {
      await submit.mutateAsync({ task_id: task.task_id });
      toast.success(
        task.approval_required
          ? `${task.title} sent for review`
          : `${task.title} marked done`,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not submit');
    }
  }

  if (tasks.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
      <div className="flex items-center gap-2 border-b border-divider/10 px-5 py-3">
        <ClipboardList className="h-4 w-4 text-accent" />
        <h2 className="text-sm font-semibold text-fg">Your tasks</h2>
      </div>
      <ul className="divide-y divide-divider/10">
        {tasks.map((t) => {
          const style = TASK_STATUS_STYLE[t.status];
          const StatusIcon = style.icon;
          const overdue =
            t.due_at &&
            t.status !== 'complete' &&
            new Date(t.due_at).getTime() < startOfToday();
          return (
            <li key={t.task_id} className="px-5 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-fg">
                      {t.title}
                    </span>
                    <Pill
                      tone={style.tone}
                      icon={<StatusIcon className="h-3 w-3" />}
                    >
                      {style.label}
                    </Pill>
                    {t.due_at ? (
                      <span
                        className={`text-[11px] tabular-nums ${
                          overdue ? 'font-semibold text-danger-soft-fg' : 'text-fg-dim'
                        }`}
                      >
                        Due {t.due_at}
                        {overdue ? ' · overdue' : ''}
                      </span>
                    ) : null}
                  </div>
                  {t.description ? (
                    <p className="mt-1 whitespace-pre-wrap text-xs text-fg-muted">
                      {t.description}
                    </p>
                  ) : null}
                  {t.status === 'changes_requested' && t.rejection_reason ? (
                    <div className="mt-2 flex items-start gap-2 rounded-token-sm border border-danger/20 bg-danger-soft/15 px-3 py-2 text-xs text-danger-soft-fg">
                      <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        <span className="font-semibold">HR said: </span>
                        {t.rejection_reason}
                      </span>
                    </div>
                  ) : null}
                  {t.submission_document_s3_key ? (
                    <a
                      href={`/api/documents/${t.submission_document_s3_key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                    >
                      <FileImage className="h-3.5 w-3.5" />
                      View what you submitted
                    </a>
                  ) : null}
                </div>
                {t.status !== 'complete' ? (
                  <div className="flex shrink-0 flex-col gap-1.5">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setSubmittingId(t.task_id)}
                    >
                      {t.submission_document_s3_key ? 'Retake' : 'Submit photo'}
                    </Button>
                    {!t.approval_required && !t.submission_document_s3_key ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => markDone(t)}
                        loading={submit.isPending}
                      >
                        Mark done
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <AddDocumentModal
                open={submittingId === t.task_id}
                personName={t.title}
                onClose={() => setSubmittingId(null)}
                onSave={(input) => onSubmit(t, input)}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function startOfToday(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
